import { useEffect, useState } from "react";
import { useSearchParams } from "react-router-dom";
import KopSurat from "@/components/KopSurat";
import { Button } from "@/components/ui/button";
import { Printer } from "lucide-react";
import { bulkFetch, listVisitors } from "@/lib/api";
import { formatHariTanggalID } from "@/lib/format";
import { useSettings } from "@/lib/useSettings";

export default function PrintBulkAbsensi() {
  const [sp] = useSearchParams();
  const settings = useSettings();
  const [items, setItems] = useState(null);

  useEffect(() => {
    const idsParam = sp.get("ids");
    const instansi = sp.get("instansi");
    const tanggal = sp.get("tanggal");
    if (idsParam) {
      const ids = idsParam.split(",").filter(Boolean);
      bulkFetch(ids).then(setItems);
    } else if (instansi && tanggal) {
      listVisitors({ instansi, date_from: tanggal, date_to: tanggal }).then(setItems);
    } else {
      setItems([]);
    }
  }, [sp]);

  if (!items) return <div className="p-10 text-center text-slate-500">Memuat dokumen…</div>;
  if (items.length === 0) return <div className="p-10 text-center text-slate-500">Tidak ada data kunjungan terpilih.</div>;

  // Build attendance rows
  const rows = [];
  items.forEach((v, vi) => {
    rows.push({ nama: v.nama_tamu, jabatan: v.jabatan_tamu, instansi: v.instansi_asal, role: "Ketua Rombongan", group: vi });
    (v.anggota_tamu || []).forEach((a) => {
      if (a?.nama) rows.push({ nama: a.nama, jabatan: a.jabatan || v.jabatan_tamu || "-", instansi: v.instansi_asal, role: "Anggota Tamu", group: vi });
    });
    const pdList = (v.daftar_pendamping || []).filter(p => p?.nama);
    if (pdList.length > 0) {
      pdList.forEach(p => rows.push({ nama: p.nama, jabatan: p.jabatan || v.jabatan_pendamping || "-", instansi: v.instansi_asal, role: "Pendamping", group: vi }));
    } else {
      (v.nama_pendamping || "").split(/[\n,]+/).map(s => s.trim()).filter(Boolean)
        .forEach(nm => rows.push({ nama: nm, jabatan: v.jabatan_pendamping || "-", instansi: v.instansi_asal, role: "Pendamping", group: vi }));
    }
  });
  while (rows.length < 15) rows.push({ nama: "", jabatan: "", instansi: "", role: "", group: -1 });

  const firstDate = items[0]?.tanggal_kunjungan;
  const samePerihal = items.every(i => i.perihal_kunjungan === items[0].perihal_kunjungan);
  const sameInstansi = items.every(i => i.instansi_asal === items[0].instansi_asal);
  const totalPeserta = items.reduce((s, v) => s + (Number(v.jumlah_tamu) || 0) + (Number(v.jumlah_pendamping) || 0), 0);
  const noSuratList = [...new Set(items.map(i => i.nomor_surat_kunjungan).filter(Boolean))].join(" · ");
  const noTugasList = [...new Set(items.map(i => i.nomor_surat_tugas).filter(Boolean))].join(" · ");

  return (
    <div className="min-h-screen bg-slate-100 py-8 print:bg-white print:py-0">
      <div className="no-print max-w-4xl mx-auto px-4 mb-4 flex justify-between items-center">
        <h2 className="text-lg font-semibold text-slate-800">Pratinjau Daftar Hadir Gabungan ({items.length} kunjungan · {totalPeserta} peserta)</h2>
        <div className="flex gap-2">
          <Button onClick={() => window.print()} className="btn-primary" data-testid="print-trigger-bulk">
            <Printer className="w-4 h-4 mr-2" /> Cetak / Simpan PDF
          </Button>
          <Button variant="outline" onClick={() => window.close()}>Tutup</Button>
        </div>
      </div>

      <div className="print-area max-w-4xl mx-auto bg-white p-10 md:p-14 shadow-xl print:shadow-none print:p-0">
        <KopSurat />
        <div className="mt-8 text-center">
          <div className="text-lg md:text-xl font-bold uppercase tracking-widest kop-surat">
            Daftar Hadir Gabungan Tamu Kunjungan
          </div>
          <div className="text-sm kop-surat mt-1">
            {noSuratList && `No. Surat Kunjungan: ${noSuratList}`}
            {noTugasList && ` · No. Surat Tugas: ${noTugasList}`}
          </div>
        </div>

        <div className="mt-6 kop-surat text-sm">
          <table className="w-full">
            <tbody>
              <tr><td className="w-44 py-1">Hari / Tanggal</td><td className="py-1">: {formatHariTanggalID(firstDate)}{items.length > 1 && !items.every(i => i.tanggal_kunjungan === firstDate) ? " (dan tanggal lainnya)" : ""}</td></tr>
              {sameInstansi && <tr><td className="py-1">Instansi</td><td className="py-1">: {items[0].instansi_asal} — {items[0].daerah_asal}</td></tr>}
              {samePerihal && <tr><td className="py-1">Perihal</td><td className="py-1">: {items[0].perihal_kunjungan}</td></tr>}
              <tr><td className="py-1">Jumlah Rombongan</td><td className="py-1">: {items.length} kunjungan ({totalPeserta} orang)</td></tr>
            </tbody>
          </table>
        </div>

        <table className="w-full mt-6 border-collapse border border-black kop-surat text-sm">
          <thead>
            <tr className="bg-slate-100">
              <th className="border border-black py-2 w-10">No.</th>
              <th className="border border-black py-2">Nama Lengkap</th>
              <th className="border border-black py-2">Jabatan</th>
              {!sameInstansi && <th className="border border-black py-2">Instansi</th>}
              <th className="border border-black py-2 w-28">Keterangan</th>
              <th className="border border-black py-2 w-40">Tanda Tangan</th>
            </tr>
          </thead>
          <tbody>
            {rows.map((n, i) => (
              <tr key={i}>
                <td className="border border-black py-2 text-center">{i + 1}</td>
                <td className="border border-black py-2 px-2">{n.nama}</td>
                <td className="border border-black py-2 px-2">{n.jabatan}</td>
                {!sameInstansi && <td className="border border-black py-2 px-2">{n.instansi}</td>}
                <td className="border border-black py-2 px-2">{n.role}</td>
                <td className="border border-black py-6 text-center">{n.nama ? `${i + 1}.` : ""}</td>
              </tr>
            ))}
          </tbody>
        </table>

        <div className="mt-10 grid grid-cols-2 gap-10 kop-surat text-sm">
          <div>
            <div>Penanggung Jawab Kegiatan,</div>
            <div className="h-20" />
            <div className="font-bold underline">
              {[...new Set(items.map(i => i.penanggung_jawab))].join(" / ")}
            </div>
          </div>
          <div className="text-right">
            <div>{settings?.kota_ttd || "Kuala Pembuang"}, {formatHariTanggalID(firstDate)}</div>
            <div>{settings?.jabatan_penandatangan || "Sekretaris DPRD Kabupaten Seruyan"},</div>
            <div className="h-20" />
            <div className="font-bold underline">{settings?.nama_penandatangan || "(..............................)"}</div>
            <div>NIP. {settings?.nip_penandatangan || ".............................."}</div>
          </div>
        </div>
      </div>
    </div>
  );
}
